import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/cn";
import { useDocuments } from "@/hooks/useDocuments";

interface FormField {
  id: string;
  label: string;
  type: "text" | "textarea" | "select" | "file" | "checkbox";
  required: boolean;
  options?: string[];
  value: string;
}

export function ApplyPage() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const { data: documents = [] } = useDocuments({ docType: "cv" });
  const defaultCv =
    documents.find((d) => d.isDefault) ?? documents[0];

  async function handleExtract() {
    if (!url.trim()) return;
    setError(null);
    setLoading(true);

    try {
      // Simulate form extraction from a job URL
      // In production, this would call apiPost("/api/apply/extract", { url })
      await new Promise((r) => setTimeout(r, 1000));
      setFields([
        {
          id: "name",
          label: "Full Name",
          type: "text",
          required: true,
          value: "John Doe",
        },
        {
          id: "email",
          label: "Email Address",
          type: "text",
          required: true,
          value: "john@example.com",
        },
        {
          id: "phone",
          label: "Phone Number",
          type: "text",
          required: true,
          value: "+1 (555) 123-4567",
        },
        {
          id: "cover_letter",
          label: "Cover Letter",
          type: "textarea",
          required: false,
          value:
            "I am writing to express my strong interest in the position…",
        },
        {
          id: "resume",
          label: "Resume / CV",
          type: "file",
          required: true,
          value: "",
        },
        {
          id: "linkedin",
          label: "LinkedIn Profile",
          type: "text",
          required: false,
          value: "https://linkedin.com/in/johndoe",
        },
        {
          id: "work_authorization",
          label: "Work Authorization",
          type: "select",
          required: true,
          options: [
            "US Citizen",
            "Green Card",
            "H1-B Visa",
            "F-1 OPT",
            "Other Visa",
            "Need Sponsorship",
          ],
          value: "US Citizen",
        },
        {
          id: "pronouns",
          label: "Pronouns",
          type: "select",
          required: false,
          options: [
            "He/Him",
            "She/Her",
            "They/Them",
            "Prefer not to say",
          ],
          value: "Prefer not to say",
        },
        {
          id: "agree_terms",
          label: "I agree to the terms and conditions",
          type: "checkbox",
          required: true,
          value: "false",
        },
      ]);
      setExtracted(true);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to extract form fields.",
      );
    } finally {
      setLoading(false);
    }
  }

  function updateField(id: string, value: string) {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, value } : f)),
    );
  }

  function handleOpenForm() {
    // In production, this would open the real application URL in a new tab
    // so the user can manually review and submit.
    if (url) {
      window.open(url, "_blank", "noopener noreferrer");
    }
  }

  return (
    <>
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Apply</h1>
        <p className="text-muted-foreground">
          Pre-fill and review application forms before submitting on the
          employer&apos;s site.
        </p>
      </header>

      {/* Step 1: Enter URL */}
      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Step 1: Enter Job URL
        </h2>
        <div className="flex gap-2">
          <input
            type="url"
            placeholder="https://company.com/careers/job/123"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleExtract();
            }}
            className="flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            onClick={handleExtract}
            disabled={loading || !url.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Extracting…
              </>
            ) : (
              "Extract Form"
            )}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-destructive">{error}</p>
        )}
      </div>

      {/* Step 2: Review & Edit */}
      {extracted && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Step 2: Review & Edit
          </h2>
          <p className="mb-4 text-xs text-muted-foreground">
            Fields have been pre-filled from your CV profile. Edit any field
            before proceeding.
          </p>

          <div className="space-y-4">
            {fields
              .filter((f) => f.type !== "checkbox")
              .map((field) => (
                <div key={field.id} className="flex flex-col gap-1.5">
                  <label
                    htmlFor={`apply-${field.id}`}
                    className="text-xs font-medium text-foreground"
                  >
                    {field.label}
                    {field.required && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </label>

                  {field.type === "textarea" ? (
                    <textarea
                      id={`apply-${field.id}`}
                      value={field.value}
                      onChange={(e) =>
                        updateField(field.id, e.target.value)
                      }
                      rows={4}
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                  ) : field.type === "select" ? (
                    <select
                      id={`apply-${field.id}`}
                      value={field.value}
                      onChange={(e) =>
                        updateField(field.id, e.target.value)
                      }
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "file" ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <input
                          id={`apply-${field.id}`}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-brand/10 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-brand"
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {defaultCv
                          ? `Apply will use default CV: ${defaultCv.originalFileName ?? defaultCv.fileName}`
                          : "Upload a default CV in Assets"}
                        {" · "}
                        <Link
                          to="/assets"
                          className="text-brand hover:underline"
                        >
                          Manage
                        </Link>
                      </span>
                    </div>
                  ) : (
                    <input
                      id={`apply-${field.id}`}
                      type={field.type}
                      value={field.value}
                      onChange={(e) =>
                        updateField(field.id, e.target.value)
                      }
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                  )}
                </div>
              ))}

            {/* Checkboxes */}
            {fields
              .filter((f) => f.type === "checkbox")
              .map((field) => (
                <div key={field.id} className="flex items-start gap-2">
                  <input
                    id={`apply-${field.id}`}
                    type="checkbox"
                    checked={field.value === "true"}
                    onChange={(e) =>
                      updateField(field.id, String(e.target.checked))
                    }
                    className="mt-0.5 rounded border-border bg-bg text-brand focus:ring-brand"
                  />
                  <label
                    htmlFor={`apply-${field.id}`}
                    className="text-xs text-foreground"
                  >
                    {field.label}
                    {field.required && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </label>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Step 3: Submit */}
      {extracted && (
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Step 3: Submit
          </h2>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">
                The application form will open in a new tab for you to review
                and submit directly on the employer&apos;s site. We never
                submit applications automatically.
              </p>
            </div>
            <button
              onClick={handleOpenForm}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              Open Application Form
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!extracted && !loading && !error && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <svg
            className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
            />
          </svg>
          <p className="text-sm text-muted-foreground">
            Paste a job URL above to extract and pre-fill the application
            form from your CV.
          </p>
          <Link
            to="/pipeline"
            className="mt-2 inline-block text-sm text-brand hover:underline"
          >
            Or browse your pipeline
          </Link>
        </div>
      )}
    </>
  );
}